import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Trophy, Users, Calendar, Dumbbell, Newspaper, Clock, ExternalLink, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { seedData } from '../lib/api';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { SponsorGrid } from '../components/SponsorGrid';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { useLocalizedNews } from '../data/useLocalizedNews';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

// Last 3 nyheter, sortert etter dato (nyeste først)

const getsports = () => [
  {
    name: 'Fotball',
    description: t('pg.Home.f4270f'),
    image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80',
    link: '/soccer',
    accent: 'from-green-500/20',
  },
  {
    name: 'Tennis',
    description: t('pg.Home.d3c106'),
    image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800&q=80',
    link: '/tennis',
    accent: 'from-yellow-500/20',
  },
  {
    name: 'Dans & Pilates',
    description: t('pg.Home.8a3768'),
    image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&q=80',
    link: '/dance-pilates',
    accent: 'from-pink-500/20',
  },
  {
    name: 'Social Run Club',
    description: t('pg.Home.2c3b53'),
    image: 'https://images.pexels.com/photos/5319373/pexels-photo-5319373.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800',
    link: '/social-run-club',
    accent: 'from-blue-500/20',
  },
];

const getstats = () => [
  { icon: Trophy, value: '1955', label: t('pg.Home.25f5e3') },
  { icon: Users, value: '500+', label: t('pg.Home.c1ced8') },
  { icon: Calendar, value: '50+', label: t('pg.Home.53032e') },
  { icon: Dumbbell, value: '16', label: t('pg.Home.230b4e') },
];

export const Home = () => {
  const { t } = useTranslation();
  const nyheter = useLocalizedNews().latest(3);
  
  useEffect(() => {
    // Seed data on first load
    seedData().catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO 
        title={t('pg.Home.ade82c')} 
        description={t('pg.Home.1b30a7')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920&q=80"
            alt={t('pg.Home.0c1872')}
            loading="eager"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
          <div className="absolute inset-0 hero-spotlight" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <p className="text-green-400 font-medium uppercase tracking-widest mb-4 animate-fade-in-up">
              {t('home.welcome')}
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-4 animate-fade-in-up animation-delay-100">
              {t('home.heroTitle1')}<br />
              <span className="text-green-400">{t('home.heroTitle2')}</span>
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-6 max-w-2xl animate-fade-in-up animation-delay-150 italic">
              {t('home.heroSubtitle')}
            </p>
            <div className="space-y-4 mb-8 max-w-2xl animate-fade-in-up animation-delay-200">
              {t('home.heroDescription').split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-base sm:text-lg text-slate-200 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <a
                href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="join-now-btn"
                aria-label={t('pg.Home.1e2e36')}
              >
                <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-green-600 shadow-lg">
                  {t('home.joinNow')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/store" data-testid="club-pride-btn" aria-label={t('pg.Home.68ab5e')}>
                <Button className="bg-white text-slate-900 font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-green-400 hover:text-white rounded-none border-2 border-white shadow-lg">
                  {t('pg.Home.98dc92')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/soccer" data-testid="explore-teams-btn">
                <Button variant="outline" className="border-white/30 text-white uppercase tracking-wider px-8 py-6 text-lg hover:bg-white/10">
                  {t('home.exploreTeams')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {getstats().map((stat, index) => (
              <div
                key={stat.label}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <stat.icon className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <p className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-1">{stat.value}</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nyheter/News Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-100 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-green-600 font-medium uppercase tracking-widest mb-4 flex items-center gap-2">
                <Newspaper className="w-4 h-4" />
                {t('home.latestNews')}
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
                {t('home.news')}
              </h2>
            </div>
            <Link 
              to="/nyheter"
              className="hidden sm:inline-flex items-center gap-2 text-green-600 font-medium hover:underline"
              data-testid="news-archive-link"
            >
              {t('home.seeAllNews')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {nyheter.map((nyhet, index) => {
              const useContainStyle = nyhet.imageStyle === 'contain' || nyhet.featured;
              return (
                <Link
                  key={nyhet.id}
                  to={`/nyheter/${nyhet.slug}`}
                  data-testid={`news-card-${nyhet.id}`}
                  className="group bg-white rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-green-500 hover:shadow-lg transition-all duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`aspect-video relative overflow-hidden ${useContainStyle ? 'bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-8' : ''}`}>
                    <img
                      src={nyhet.image}
                      alt={nyhet.title}
                      loading="lazy"
                      className={`${useContainStyle ? 'max-h-full max-w-full object-contain' : 'w-full h-full object-cover'} group-hover:scale-105 transition-transform duration-500`}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded uppercase">
                        {nyhet.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-3">
                      <Clock className="w-3 h-3" />
                      {nyhet.date}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 group-hover:text-green-600 transition-colors line-clamp-2">
                      {nyhet.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 text-sm line-clamp-3">
                      {nyhet.excerpt}
                    </p>
                    <span className="inline-flex items-center text-green-600 font-medium text-sm mt-4">
                      {t('home.readMore')}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          
          {/* Mobile link to archive */}
          <div className="mt-8 text-center sm:hidden">
            <Link 
              to="/nyheter"
              className="inline-flex items-center gap-2 text-green-600 font-medium"
            >
              {t('home.seeAllNews')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Sports Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-green-600 font-medium uppercase tracking-widest mb-4">{t('home.ourOffers')}</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('home.findYourSport')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {getsports().map((sport, index) => (
              <Link
                key={sport.name}
                to={sport.link}
                data-testid={`sport-card-${sport.name.toLowerCase()}`}
                className="group relative overflow-hidden rounded-xl aspect-[4/5] animate-fade-in-up shadow-lg"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img
                  src={sport.image}
                  alt={`${sport.name} - Veitvet Sportsklubb`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${sport.accent} via-transparent to-transparent opacity-50`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 group-hover:text-green-400 transition-colors">
                    {sport.name}
                  </h3>
                  <p className="text-slate-200 text-sm mb-4">{sport.description}</p>
                  <span className="inline-flex items-center text-green-400 font-medium uppercase tracking-wider text-sm">
                    {t('home.explore')}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Rentals CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('home.rental')}</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white mb-6">
                {t('home.hostYourEvent')}<br />
                <span className="text-green-400">{t('home.nextEvent')}</span>
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                {t('home.rentalDescription')}
              </p>
              <Link to="/rentals" data-testid="view-spaces-btn">
                <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                  {t('home.viewSpaces')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80"
                alt={t('pg.Home.3ee03c')}
                loading="lazy"
                className="rounded-lg aspect-square object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80"
                alt={t('pg.Home.c805c1')}
                loading="lazy"
                className="rounded-lg aspect-square object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Store CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-green-600 font-medium uppercase tracking-widest mb-4">{t('home.shopNow')}</p>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white mb-6">
            {t('home.equipYourself')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            {t('home.shopDescription')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://cometsport.no/products/index/185?type=category"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shop-comet-btn"
            >
              <Button className="bg-green-600 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-green-700">
                {t('home.clubCollection')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/pricing" data-testid="membership-prices-btn">
              <Button variant="outline" className="border-slate-300 text-slate-700 uppercase tracking-wider px-8 py-6 text-lg hover:bg-slate-100 dark:bg-slate-800">
                {t('home.seePrices')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sponsorer Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-600 font-medium uppercase tracking-widest mb-4">{t('home.thankYou')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('home.ourSponsors')}
            </h2>
          </div>

          <SponsorGrid />
        </div>
      </section>

      {/* Sponsor CTA Banner */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-y border-green-500/20">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/bli-sponsor"
            data-testid="bli-sponsor-cta-btn"
            className="group block bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 rounded-2xl p-6 sm:p-8 transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-0.5"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-7 h-7 text-white" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-white/80 text-xs uppercase tracking-widest mb-1 font-medium">
                    {t('pg.Home.d2106b')}
                  </p>
                  <p className="text-white text-xl sm:text-2xl font-bold tracking-tight">
                    {t('pg.Home.1c7252')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-5 py-3 group-hover:bg-white/20 transition-colors flex-shrink-0">
                <span className="text-white font-bold uppercase tracking-wider text-sm">{t('news.readMore')}</span>
                <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default Home;
